import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography
} from '@mui/material';
import {
  Button
} from "@material-ui/core";
import { Assignment as AssignmentIcon } from '@mui/icons-material';
import EvaluationCoursService from '../../services/EvaluationCoursService';

const orderOptions = [
  { value: 'sequential', label: 'Séquentiel' },
  { value: 'random_by_matiere', label: 'Aléatoire par matière' },
  { value: 'random_without_matiere', label: 'Aléatoire total' }
];

const initialForm = {
  title: '',
  class_ids: [],
  description: '',
  question_order_mode: 'sequential'
};

const EvaluationAdd = ({ open, evaluation, classes = [], onClose, onSaved }) => {
  const [formData, setFormData] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setFormData(evaluation ? {
        title: evaluation.title || '',
        class_ids: Array.isArray(evaluation.class_ids) ? evaluation.class_ids.map(Number) : [],
        description: evaluation.description || '',
        question_order_mode: evaluation.question_order_mode || 'sequential'
      } : initialForm);
      setError('');
    }
  }, [open, evaluation]);

  const handleSave = async () => {
    if (!formData.title.trim()) {
      setError('Le titre du test est requis');
      return;
    }

    if (!formData.class_ids.length) {
      setError('Veuillez sélectionner au moins une classe');
      return;
    }

    setSaving(true);
    setError('');

    const payload = {
      title: formData.title.trim(),
      class_ids: formData.class_ids,
      description: formData.description,
      question_order_mode: formData.question_order_mode
    };

    const result = evaluation
      ? await EvaluationCoursService.updateEvaluation(evaluation.id, payload)
      : await EvaluationCoursService.createEvaluation(payload);

    setSaving(false);

    if (result?.status === true) {
      onSaved?.(result.data);
      return;
    }

    setError(result?.message || "Erreur lors de l'enregistrement");
  };

  return (
    <Dialog
      open={open}
      onClose={() => !saving && onClose?.()}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <AssignmentIcon color="primary" />
          <Box>
            <Typography variant="h6" fontWeight="700">
              {evaluation ? 'Modifier une évaluation' : 'Nouvelle évaluation'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Gestion du test et des classes concernées
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ bgcolor: '#F5F7FA', pt: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 0.5 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            label="Titre du test"
            value={formData.title}
            onChange={(event) => setFormData({ ...formData, title: event.target.value })}
            fullWidth
            required
            disabled={saving}
          />

          <FormControl fullWidth required>
            <InputLabel>Classes</InputLabel>
            <Select
              multiple
              value={formData.class_ids}
              onChange={(event) => {
                const value = event.target.value;
                setFormData({
                  ...formData,
                  class_ids: typeof value === 'string' ? value.split(',').map(Number) : value.map(Number)
                });
              }}
              input={<OutlinedInput label="Classes" />}
              disabled={saving}
              renderValue={(selected) => selected
                .map((id) => classes.find((classe) => Number(classe.id) === Number(id))?.nom)
                .filter(Boolean)
                .join(', ')
              }
            >
              {classes.map((classe) => (
                <MenuItem key={classe.id} value={Number(classe.id)}>
                  {classe.nom}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Description"
            value={formData.description}
            onChange={(event) => setFormData({ ...formData, description: event.target.value })}
            multiline
            rows={4}
            fullWidth
            disabled={saving}
          />

          <FormControl fullWidth>
            <InputLabel>Ordre des questions</InputLabel>
            <Select
              value={formData.question_order_mode}
              label="Ordre des questions"
              onChange={(event) => setFormData({ ...formData, question_order_mode: event.target.value })}
              disabled={saving}
            >
              {orderOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Annuler
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving}
          startIcon={saving ? <CircularProgress size={18} /> : null}
        >
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EvaluationAdd;
